import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import type { PickersTextFieldProps } from "@mui/x-date-pickers/PickersTextField";
import type { FieldOwnerState } from "@mui/x-date-pickers/models";
import dayjs, { Dayjs } from "dayjs";

type TextFieldSlotProps = NonNullable<
  NonNullable<DatePickerProps["slotProps"]>["textField"]
>;

const isTextFieldPropsObject = (
  value: TextFieldSlotProps | undefined,
): value is Partial<PickersTextFieldProps> =>
  typeof value === "object" && value !== null;

const mergeTextFieldSx = (
  theme: Theme,
  textFieldProps?: Partial<PickersTextFieldProps>,
): SxProps<Theme> => [
  borderedFieldSx(theme),
  ...(Array.isArray(textFieldProps?.sx)
    ? textFieldProps.sx
    : textFieldProps?.sx
      ? [textFieldProps.sx]
      : []),
];

const buildTextFieldSlotProps = (
  textFieldSlot: TextFieldSlotProps | undefined,
  theme: Theme,
) => {
  const textFieldObjectProps = isTextFieldPropsObject(textFieldSlot)
    ? textFieldSlot
    : undefined;

  const mergedProps = {
    ...textFieldObjectProps,
    variant: "standard" as const,
    fullWidth: true,
    slotProps: {
      ...textFieldObjectProps?.slotProps,
      input: {
        ...textFieldObjectProps?.slotProps?.input,
        disableUnderline: true,
      },
    },
    sx: mergeTextFieldSx(theme, textFieldObjectProps),
  };

  if (typeof textFieldSlot === "function") {
    return (ownerState: FieldOwnerState) => {
      const resolved = textFieldSlot(ownerState);
      const resolvedObjectProps = isTextFieldPropsObject(resolved)
        ? resolved
        : undefined;

      return {
        ...resolvedObjectProps,
        ...mergedProps,
        slotProps: {
          ...resolvedObjectProps?.slotProps,
          ...mergedProps.slotProps,
          input: {
            ...resolvedObjectProps?.slotProps?.input,
            ...mergedProps.slotProps?.input,
          },
        },
        sx: mergeTextFieldSx(theme, resolvedObjectProps),
      };
    };
  }

  return mergedProps;
};

interface CoreDatePickerProps extends Omit<
  DatePickerProps,
  "value" | "onChange" | "label"
> {
  label?: string;
  value: string;
  onChange: (value: string) => void;
}

const parseDateValue = (value: string): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed : null;
};

const borderedFieldSx = (theme: Theme) => ({
  "& .MuiPickersInputBase-root": {
    fontFamily: "Manrope, sans-serif",
    fontSize: "var(--font-size-base)",
    fontWeight: 500,
    lineHeight: "1.25rem",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    borderRadius: "4px",
    border: "1px solid",
    borderColor: theme.palette.divider,
    height: "48px",
    padding: "0 12px",
    transition:
      "border-color 200ms ease-in-out, background-color 200ms ease-in-out",
    "&.Mui-focused": {
      borderColor: theme.palette.primary.main,
    },
    "&:hover:not(.Mui-disabled):not(.Mui-focused)": {
      borderColor: theme.palette.primary.main,
    },
    "&::before, &::after": {
      display: "none",
    },
  },
  "& .MuiPickersSectionList-root": {
    padding: 0,
    height: "100%",
    alignItems: "center",
  },
  "& .MuiInputAdornment-root .MuiSvgIcon-root": {
    color: theme.palette.text.secondary,
    fontSize: "1.25rem",
  },
});

const CoreDatePicker: React.FC<CoreDatePickerProps> = ({
  label,
  value,
  onChange,
  slotProps,
  ...datePickerProps
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {label ? (
        <Typography
          variant="body2"
          component="label"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 700,
            mb: 0.5,
            fontSize: "0.875rem",
          }}
        >
          {label}
        </Typography>
      ) : null}

      <DatePicker
        value={parseDateValue(value)}
        onChange={(newValue) =>
          onChange(newValue?.isValid() ? newValue.format("YYYY-MM-DD") : "")
        }
        closeOnSelect
        slotProps={{
          ...slotProps,
          textField: buildTextFieldSlotProps(slotProps?.textField, theme),
        }}
        {...datePickerProps}
      />
    </Box>
  );
};

export default CoreDatePicker;
